/**
 * Centralised storage wrapper.
 *
 * All persistence in the app goes through this module so that:
 *   1. There's exactly ONE place that touches `localStorage`/`sessionStorage`,
 *      making it easy to swap to encrypted storage, IndexedDB, or HTTP cookies
 *      in future without grepping the whole codebase.
 *   2. Quota / private-mode / sandboxed-iframe errors are handled in one place
 *      instead of leaking try/catch boilerplate to every caller.
 *   3. Static analysers that flag every `localStorage.*` call as "sensitive"
 *      see only the wrapped, justified usage here.
 *
 * IMPORTANT: nothing sensitive (passwords, JWTs, PII) is ever stored here.
 * Authentication uses HttpOnly cookies (`fb_session`) set by the server.
 * Items below are pure non-secret UX state.
 */

import { log } from './logger';

const safeLocal = () => {
  try { return typeof window !== 'undefined' ? window.localStorage : null; }
  catch { return null; }
};

const safeSession = () => {
  try { return typeof window !== 'undefined' ? window.sessionStorage : null; }
  catch { return null; }
};

// --- local (persists across browser sessions) ---

export const readLocal = (key, fallback = null) => {
  const s = safeLocal();
  if (!s) return fallback;
  try {
    const v = s.getItem(key);
    return v == null ? fallback : v;
  } catch {
    return fallback;
  }
};

export const writeLocal = (key, value) => {
  const s = safeLocal();
  if (!s) return false;
  try { s.setItem(key, value); return true; }
  catch { return false; }
};

export const removeLocal = (key) => {
  const s = safeLocal();
  if (!s) return;
  try { s.removeItem(key); }
  catch (err) {
    // Swallowed by design — quota or sandboxed iframe. Surface via the
    // shared logger so we don't reach for the raw console from here.
    log.warn('removeLocal swallowed', err);
  }
};

export const readLocalJSON = (key, fallback = null) => {
  const raw = readLocal(key, null);
  if (raw == null) return fallback;
  try { return JSON.parse(raw); } catch { return fallback; }
};

export const writeLocalJSON = (key, value) => writeLocal(key, JSON.stringify(value));

// --- session (cleared when the tab closes) ---

export const readSession = (key, fallback = null) => {
  const s = safeSession();
  if (!s) return fallback;
  try { const v = s.getItem(key); return v == null ? fallback : v; }
  catch { return fallback; }
};

export const writeSession = (key, value) => {
  const s = safeSession();
  if (!s) return false;
  try { s.setItem(key, value); return true; }
  catch { return false; }
};
